import Swiper from "./swiper-bundle.esm.browser.min.js";

/**
 * JKL PDP Principi Attivi — mobile slider
 *
 * The ingredient cards are a CSS grid from 768px up. Below that breakpoint the
 * grid becomes a Swiper slider with one card per view and pagination bullets.
 *
 * Swiper 7 has no per-breakpoint `enabled` option, so the instance is created
 * and destroyed from a matchMedia listener instead.
 */

const MOBILE_QUERY = "(max-width: 767.98px)";

class JklPrincipiAttiviSlider extends HTMLElement {
  constructor() {
    super();

    this.swiper = null;
    this.mediaQuery = window.matchMedia(MOBILE_QUERY);

    this.handleBreakpoint = this.handleBreakpoint.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.itemsCount = parseInt(this.dataset.itemsCount, 10) || 0;
    this.swiperEl = this.querySelector(".jkl-pdp-principi-attivi__slider");
    this.paginationEl = this.querySelector(
      ".jkl-pdp-principi-attivi__pagination",
    );

    if (!this.swiperEl || this.itemsCount < 1) return;

    this.handleBreakpoint();

    if (typeof this.mediaQuery.addEventListener === "function") {
      this.mediaQuery.addEventListener("change", this.handleBreakpoint);
    } else {
      // Safari < 14
      this.mediaQuery.addListener(this.handleBreakpoint);
    }

    document.addEventListener("shopify:block:select", this.handleBlockSelect);
  }

  disconnectedCallback() {
    if (typeof this.mediaQuery.removeEventListener === "function") {
      this.mediaQuery.removeEventListener("change", this.handleBreakpoint);
    } else {
      this.mediaQuery.removeListener(this.handleBreakpoint);
    }

    document.removeEventListener(
      "shopify:block:select",
      this.handleBlockSelect,
    );
    this.destroySwiper();
  }

  handleBreakpoint() {
    if (this.mediaQuery.matches) {
      this.initSwiper();
    } else {
      this.destroySwiper();
    }
  }

  initSwiper() {
    if (this.swiper) return;

    this.swiper = new Swiper(this.swiperEl, {
      slidesPerView: 1,
      spaceBetween: 16,
      speed: 400,
      watchOverflow: true,

      pagination:
        this.paginationEl && this.itemsCount > 1
          ? {
              el: this.paginationEl,
              clickable: true,
              bulletClass: "swiper-pagination-bullet",
              bulletActiveClass: "swiper-pagination-bullet-active",
            }
          : false,

      a11y: {
        enabled: true,
        prevSlideMessage: "Previous ingredient",
        nextSlideMessage: "Next ingredient",
      },

      observer: true,
      observeParents: true,
    });
  }

  destroySwiper() {
    if (!this.swiper) return;

    // cleanStyles: true removes the inline transform/width Swiper adds, so the
    // CSS grid takes over again on desktop.
    this.swiper.destroy(true, true);
    this.swiper = null;
  }

  handleBlockSelect(event) {
    if (!this.swiper) return;
    if (!event.detail || event.detail.sectionId !== this.sectionId) return;

    const slides = Array.from(
      this.querySelectorAll(".jkl-pdp-principi-attivi__item"),
    );
    const index = slides.findIndex(
      (slide) =>
        slide.dataset.shopifyEditorBlock &&
        slide.dataset.shopifyEditorBlock.indexOf(event.detail.blockId) !== -1,
    );

    if (index > -1) this.swiper.slideTo(index);
  }
}

if (!customElements.get("jkl-principi-attivi-slider")) {
  customElements.define("jkl-principi-attivi-slider", JklPrincipiAttiviSlider);
}
